import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Stack,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Slider,
  Switch,
  Tooltip,
  IconButton,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  ArrowBack,
  Save,
  SettingsSuggest,
  CloudUpload,
  Science,
} from '@mui/icons-material';
import { clientsAPI, publicFileUrl } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import { MEDIA_CONTENT_PILLAR_IDS, MEDIA_CONTENT_PILLAR_LABELS } from '../config/contentPillars';
import { DataTable } from '../components/primitives';

const PLATFORM_OPTIONS = [
  'instagram',
  'facebook',
  'linkedin',
  'youtube',
  'x',
  'tumblr',
  'pinterest',
];

export default function ClientOnboarding() {
  const { clientId: clientIdParam } = useParams();
  const navigate = useNavigate();
  const { showError, showSuccess } = useToast();
  const clientId = parseInt(clientIdParam, 10);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [clientName, setClientName] = useState('');
  const [watermarkUploadMode, setWatermarkUploadMode] = useState('manual');

  const [brandVoice, setBrandVoice] = useState('');
  const [captionGuardrails, setCaptionGuardrails] = useState('');
  const [captionLearningEnabled, setCaptionLearningEnabled] = useState(true);
  const [livePillars, setLivePillars] = useState([]);
  const [roadmapPillars, setRoadmapPillars] = useState([]);
  const [tonePresetsJson, setTonePresetsJson] = useState('{}');
  const [primaryPlatform, setPrimaryPlatform] = useState('instagram');
  const [platformOrderStr, setPlatformOrderStr] = useState(
    'instagram, facebook, linkedin, youtube, x',
  );
  const [platformNotes, setPlatformNotes] = useState('');
  const [anchorHashtagsStr, setAnchorHashtagsStr] = useState('');
  const [geminiModel, setGeminiModel] = useState('');
  const [promptOverridesJson, setPromptOverridesJson] = useState('{}');

  const [wm, setWm] = useState(null);
  const [wmOpacity, setWmOpacity] = useState(0.85);
  const [wmPosition, setWmPosition] = useState('corner');
  const [wmCorner, setWmCorner] = useState('bottom_right');
  const [wmEnabled, setWmEnabled] = useState(true);
  const [logoFile, setLogoFile] = useState(null);

  const [qaRuns, setQaRuns] = useState([]);
  const [qaRunLabel, setQaRunLabel] = useState('');
  const [qaRunNotes, setQaRunNotes] = useState('');
  const [qaRunRating, setQaRunRating] = useState('');
  const [qaMediaIdsStr, setQaMediaIdsStr] = useState('');
  const [qaSaving, setQaSaving] = useState(false);

  const load = useCallback(async () => {
    if (!Number.isFinite(clientId) || clientId < 1) {
      showError('Invalid client');
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [client, profile, wmt] = await Promise.all([
        clientsAPI.getClient(clientId),
        clientsAPI.getClientAiProfile(clientId),
        clientsAPI.getWatermarkTemplate(clientId),
      ]);
      let qaList = [];
      try {
        qaList = await clientsAPI.listCaptionQualityRuns(clientId, 30);
      } catch {
        qaList = [];
      }
      setClientName(client?.name || `Client ${clientId}`);
      setWatermarkUploadMode(client?.watermark_upload_mode || 'manual');

      setBrandVoice(profile?.brand_voice || '');
      setCaptionGuardrails(profile?.caption_guardrails || '');
      setCaptionLearningEnabled(
        profile?.caption_learning_enabled !== false,
      );
      const ap = profile?.active_pillars;
      if (ap && typeof ap === 'object' && !Array.isArray(ap)) {
        setLivePillars(Array.isArray(ap.live) ? ap.live : ap.current || []);
        setRoadmapPillars(Array.isArray(ap.roadmap) ? ap.roadmap : ap.planned || []);
      } else if (Array.isArray(ap)) {
        setLivePillars(ap);
        setRoadmapPillars([]);
      }
      setTonePresetsJson(
        profile?.tone_presets != null
          ? JSON.stringify(profile.tone_presets, null, 2)
          : '{}',
      );
      const pp = profile?.platform_preferences;
      if (pp && typeof pp === 'object') {
        setPrimaryPlatform(pp.primary || 'instagram');
        const ord = pp.priority_order || pp.order;
        if (Array.isArray(ord) && ord.length) {
          setPlatformOrderStr(ord.join(', '));
        }
        setPlatformNotes(pp.notes || '');
      }
      setAnchorHashtagsStr(
        Array.isArray(profile?.anchor_hashtags)
          ? profile.anchor_hashtags.join(', ')
          : '',
      );
      setGeminiModel(profile?.gemini_model_version || '');
      setPromptOverridesJson(
        profile?.prompt_overrides != null
          ? JSON.stringify(profile.prompt_overrides, null, 2)
          : '{}',
      );

      setWm(wmt);
      if (wmt) {
        setWmOpacity(Number(wmt.opacity) ?? 0.85);
        setWmPosition(wmt.position_mode || 'corner');
        setWmCorner(wmt.corner_anchor || 'bottom_right');
        setWmEnabled(wmt.enabled !== false);
      }
      setQaRuns(Array.isArray(qaList) ? qaList : []);
    } catch (e) {
      showError(e?.response?.data?.detail || e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [clientId, showError]);

  useEffect(() => {
    load();
  }, [load]);

  const togglePillar = (pillar, bucket) => {
    if (bucket === 'live') {
      setLivePillars((prev) => {
        const has = prev.includes(pillar);
        const next = has ? prev.filter((p) => p !== pillar) : [...prev, pillar];
        if (!has) {
          setRoadmapPillars((r) => r.filter((p) => p !== pillar));
        }
        return next;
      });
    } else {
      setRoadmapPillars((prev) => {
        const has = prev.includes(pillar);
        const next = has ? prev.filter((p) => p !== pillar) : [...prev, pillar];
        if (!has) {
          setLivePillars((r) => r.filter((p) => p !== pillar));
        }
        return next;
      });
    }
  };

  const handleSaveAi = async () => {
    setSaving(true);
    try {
      let tone;
      let po;
      try {
        tone = JSON.parse(tonePresetsJson.trim() || '{}');
      } catch {
        showError('Tone presets must be valid JSON');
        setSaving(false);
        return;
      }
      try {
        po = JSON.parse(promptOverridesJson.trim() || '{}');
      } catch {
        showError('Prompt overrides must be valid JSON');
        setSaving(false);
        return;
      }
      const order = platformOrderStr
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      await clientsAPI.updateClientAiProfile(clientId, {
        brand_voice: brandVoice.trim() || null,
        caption_guardrails: captionGuardrails.trim() || null,
        caption_learning_enabled: captionLearningEnabled,
        active_pillars: { live: livePillars, roadmap: roadmapPillars },
        tone_presets: tone && typeof tone === 'object' && Object.keys(tone).length ? tone : null,
        platform_preferences: {
          primary: primaryPlatform,
          priority_order: order,
          notes: platformNotes.trim() || undefined,
        },
        anchor_hashtags: anchorHashtagsStr
          .split(',')
          .map((t) => t.trim().replace(/^#/, ''))
          .filter(Boolean),
        gemini_model_version: geminiModel.trim() || null,
        prompt_overrides: po && typeof po === 'object' && Object.keys(po).length ? po : null,
      });
      await clientsAPI.updateClient(clientId, {
        watermark_upload_mode: watermarkUploadMode,
      });
      showSuccess('Client AI profile saved');
      await load();
    } catch (e) {
      showError(e?.response?.data?.detail || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleLogCaptionQaRun = async () => {
    const label = qaRunLabel.trim();
    if (!label) {
      showError('Enter a run label (e.g. DDS batch 2026-04-08).');
      return;
    }
    setQaSaving(true);
    try {
      const ids = qaMediaIdsStr
        .split(',')
        .map((s) => parseInt(s.trim(), 10))
        .filter((n) => Number.isFinite(n) && n > 0);
      let rating = null;
      if (qaRunRating !== '' && qaRunRating != null) {
        const r = parseInt(String(qaRunRating), 10);
        if (Number.isFinite(r) && r >= 1 && r <= 5) rating = r;
      }
      await clientsAPI.createCaptionQualityRun(clientId, {
        run_label: label,
        media_asset_ids: ids.length ? ids : null,
        notes: qaRunNotes.trim() || null,
        tone_rating: rating,
        snapshot_profile: true,
      });
      showSuccess('Caption QA run logged');
      setQaRunLabel('');
      setQaRunNotes('');
      setQaRunRating('');
      setQaMediaIdsStr('');
      const rows = await clientsAPI.listCaptionQualityRuns(clientId, 30);
      setQaRuns(Array.isArray(rows) ? rows : []);
    } catch (e) {
      showError(e?.response?.data?.detail || e.message || 'Failed to log run');
    } finally {
      setQaSaving(false);
    }
  };

  const handleSaveWatermark = async () => {
    setSaving(true);
    try {
      if (logoFile) {
        await clientsAPI.uploadWatermarkLogo(clientId, logoFile);
        setLogoFile(null);
      }
      await clientsAPI.updateWatermarkTemplate(clientId, {
        position_mode: wmPosition,
        corner_anchor: wmCorner,
        opacity: wmOpacity,
        enabled: wmEnabled,
      });
      showSuccess('Watermark settings saved');
      await load();
    } catch (e) {
      showError(e?.response?.data?.detail || e.message || 'Watermark save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  const logoPreview = wm?.logo_url ? publicFileUrl(wm.logo_url) : null;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#0d1117', pb: 6 }}>
      <Box
        sx={{
          borderBottom: '1px solid #30363d',
          bgcolor: '#161b22',
          px: 3,
          py: 2,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Tooltip title="Back to clients">
            <IconButton onClick={() => navigate('/clients')} sx={{ color: '#f0f6fc' }}>
              <ArrowBack />
            </IconButton>
          </Tooltip>
          <SettingsSuggest sx={{ color: '#58a6ff', fontSize: 32 }} />
          <Box>
            <Typography variant="h5" sx={{ color: '#f0f6fc', fontWeight: 700 }}>
              Client onboarding
            </Typography>
            <Typography variant="body2" sx={{ color: '#8b949e' }}>
              {clientName} · Brand voice, pillars, tone, hashtags, platform priority, watermark
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Box sx={{ maxWidth: 900, mx: 'auto', px: 3, pt: 3 }}>
        <Stack spacing={3}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ color: '#f0f6fc', mb: 2 }}>
              AI & brand profile
            </Typography>
            <Typography variant="body2" sx={{ color: '#8b949e', mb: 2 }}>
              Drives Gemini caption prompts (Task 4.3). Same flow for every client.
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={4}
              label="Brand voice"
              value={brandVoice}
              onChange={(e) => setBrandVoice(e.target.value)}
              sx={{ mb: 2, '& .MuiInputLabel-root': { color: '#8b949e' } }}
              InputProps={{ sx: { color: '#f0f6fc' } }}
            />
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Caption guardrails (industry & anti-drift)"
              value={captionGuardrails}
              onChange={(e) => setCaptionGuardrails(e.target.value)}
              placeholder="e.g. We are a residential tree care and landscape company. Never use generic entrepreneur, laptop, or digital-nomad angles unless the user explicitly asks."
              helperText="Applied to every Gemini caption run for this client, with each asset’s optional post intent."
              FormHelperTextProps={{ sx: { color: '#6e7681', maxWidth: '100%' } }}
              sx={{ mb: 2, '& .MuiInputLabel-root': { color: '#8b949e' } }}
              InputProps={{ sx: { color: '#f0f6fc' } }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={captionLearningEnabled}
                  onChange={(e) => setCaptionLearningEnabled(e.target.checked)}
                  sx={{ color: '#8b949e' }}
                />
              }
              label={
                <Box>
                  <Typography sx={{ color: '#c9d1d9' }}>
                    Learn from my caption edits
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#6e7681', display: 'block' }}>
                    When on, edits you make to Gemini captions before publish improve future
                    suggestions. Turn off to stop logging and preference injection.
                  </Typography>
                </Box>
              }
              sx={{ mb: 2, alignItems: 'flex-start', ml: 0 }}
            />

            <Typography variant="subtitle2" sx={{ color: '#8b949e', mb: 1 }}>
              Active content pillars
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ mb: 2 }}>
              <Box>
                <Typography variant="caption" sx={{ color: '#58a6ff' }}>
                  Live
                </Typography>
                <FormGroup>
                  {MEDIA_CONTENT_PILLAR_IDS.map((id) => (
                    <FormControlLabel
                      key={`live-${id}`}
                      control={
                        <Checkbox
                          size="small"
                          checked={livePillars.includes(id)}
                          onChange={() => togglePillar(id, 'live')}
                          sx={{ color: '#8b949e' }}
                        />
                      }
                      label={MEDIA_CONTENT_PILLAR_LABELS[id] || id}
                      sx={{ color: '#c9d1d9' }}
                    />
                  ))}
                </FormGroup>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: '#d29922' }}>
                  Roadmap
                </Typography>
                <FormGroup>
                  {MEDIA_CONTENT_PILLAR_IDS.map((id) => (
                    <FormControlLabel
                      key={`road-${id}`}
                      control={
                        <Checkbox
                          size="small"
                          checked={roadmapPillars.includes(id)}
                          onChange={() => togglePillar(id, 'roadmap')}
                          sx={{ color: '#8b949e' }}
                        />
                      }
                      label={MEDIA_CONTENT_PILLAR_LABELS[id] || id}
                      sx={{ color: '#c9d1d9' }}
                    />
                  ))}
                </FormGroup>
              </Box>
            </Stack>

            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Tone presets (JSON object)"
              value={tonePresetsJson}
              onChange={(e) => setTonePresetsJson(e.target.value)}
              sx={{ mb: 2 }}
              InputProps={{ sx: { color: '#f0f6fc', fontFamily: 'ui-monospace' } }}
            />

            <TextField
              fullWidth
              label="Anchor hashtags (comma-separated, no # required)"
              value={anchorHashtagsStr}
              onChange={(e) => setAnchorHashtagsStr(e.target.value)}
              sx={{ mb: 2 }}
              InputProps={{ sx: { color: '#f0f6fc' } }}
            />

            <Typography variant="subtitle2" sx={{ color: '#8b949e', mb: 1 }}>
              Platform priority
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel sx={{ color: '#8b949e' }}>Primary platform</InputLabel>
                <Select
                  label="Primary platform"
                  value={primaryPlatform}
                  onChange={(e) => setPrimaryPlatform(e.target.value)}
                  sx={{ color: '#f0f6fc' }}
                >
                  {PLATFORM_OPTIONS.map((p) => (
                    <MenuItem key={p} value={p}>
                      {p}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Priority order (comma-separated)"
                value={platformOrderStr}
                onChange={(e) => setPlatformOrderStr(e.target.value)}
                InputProps={{ sx: { color: '#f0f6fc' } }}
              />
            </Stack>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Platform notes (optional)"
              value={platformNotes}
              onChange={(e) => setPlatformNotes(e.target.value)}
              sx={{ mb: 2 }}
              InputProps={{ sx: { color: '#f0f6fc' } }}
            />

            <TextField
              fullWidth
              label="Gemini model version (optional, e.g. gemini-2.5-flash)"
              value={geminiModel}
              onChange={(e) => setGeminiModel(e.target.value)}
              sx={{ mb: 2 }}
              InputProps={{ sx: { color: '#f0f6fc' } }}
            />

            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Prompt overrides (JSON)"
              value={promptOverridesJson}
              onChange={(e) => setPromptOverridesJson(e.target.value)}
              sx={{ mb: 2 }}
              InputProps={{ sx: { color: '#f0f6fc', fontFamily: 'ui-monospace' } }}
            />

            <FormControl size="small" sx={{ minWidth: 260, mb: 2 }}>
              <InputLabel sx={{ color: '#8b949e' }}>Watermark on upload</InputLabel>
              <Select
                label="Watermark on upload"
                value={watermarkUploadMode}
                onChange={(e) => setWatermarkUploadMode(e.target.value)}
                sx={{ color: '#f0f6fc' }}
              >
                <MenuItem value="manual">Manual (after upload)</MenuItem>
                <MenuItem value="auto">Auto (enqueue on image upload)</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <Save />}
              disabled={saving}
              onClick={handleSaveAi}
            >
              Save AI & brand profile
            </Button>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ color: '#f0f6fc', mb: 1 }}>
              Watermark template
            </Typography>
            <Typography variant="body2" sx={{ color: '#8b949e', mb: 2 }}>
              PNG logo and layout. Used by library watermarks and auto-watermark when enabled.
            </Typography>

            {logoPreview && (
              <Box
                component="img"
                src={logoPreview}
                alt="Logo"
                sx={{ maxHeight: 80, mb: 2, borderRadius: 1, border: '1px solid #30363d' }}
              />
            )}

            <Button variant="outlined" component="label" startIcon={<CloudUpload />} sx={{ mb: 2 }}>
              Upload logo (PNG)
              <input
                type="file"
                hidden
                accept="image/png"
                onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
              />
            </Button>
            {logoFile && (
              <Typography variant="caption" sx={{ color: '#8b949e', display: 'block', mb: 1 }}>
                Selected: {logoFile.name}
              </Typography>
            )}

            <FormControl size="small" sx={{ minWidth: 200, mr: 2, mb: 2 }}>
              <InputLabel sx={{ color: '#8b949e' }}>Position</InputLabel>
              <Select
                label="Position"
                value={wmPosition}
                onChange={(e) => setWmPosition(e.target.value)}
                sx={{ color: '#f0f6fc' }}
              >
                <MenuItem value="corner">Corner</MenuItem>
                <MenuItem value="center">Center</MenuItem>
              </Select>
            </FormControl>
            {wmPosition === 'corner' && (
              <FormControl size="small" sx={{ minWidth: 200, mb: 2 }}>
                <InputLabel sx={{ color: '#8b949e' }}>Corner</InputLabel>
                <Select
                  label="Corner"
                  value={wmCorner}
                  onChange={(e) => setWmCorner(e.target.value)}
                  sx={{ color: '#f0f6fc' }}
                >
                  <MenuItem value="top_left">top_left</MenuItem>
                  <MenuItem value="top_right">top_right</MenuItem>
                  <MenuItem value="bottom_left">bottom_left</MenuItem>
                  <MenuItem value="bottom_right">bottom_right</MenuItem>
                </Select>
              </FormControl>
            )}

            <Typography variant="caption" sx={{ color: '#8b949e', display: 'block' }}>
              Opacity
            </Typography>
            <Slider
              value={wmOpacity}
              min={0.1}
              max={1}
              step={0.05}
              onChange={(_, v) => setWmOpacity(v)}
              sx={{ maxWidth: 360, mb: 2 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={wmEnabled}
                  onChange={(e) => setWmEnabled(e.target.checked)}
                  sx={{ color: '#8b949e' }}
                />
              }
              label={<Typography sx={{ color: '#c9d1d9' }}>Watermark enabled</Typography>}
              sx={{ mb: 2 }}
            />

            <Divider sx={{ borderColor: '#30363d', my: 2 }} />

            <Button
              variant="contained"
              color="secondary"
              startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <Save />}
              disabled={saving}
              onClick={handleSaveWatermark}
            >
              Save watermark template
            </Button>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Science sx={{ color: '#79c0ff' }} />
              <Typography variant="h6" sx={{ color: '#f0f6fc' }}>
                Caption quality testing (4.6)
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ color: '#8b949e', mb: 2 }}>
              After you upload a DDS test batch in Gallery and generate captions, log a run here with
              optional tone rating (1–5) and asset IDs. The DB stores a snapshot of brand voice and
              pillars for comparison as you iterate prompts.
            </Typography>
            <Stack spacing={2} sx={{ mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="Run label"
                placeholder="e.g. DDS pilot batch A"
                value={qaRunLabel}
                onChange={(e) => setQaRunLabel(e.target.value)}
                InputProps={{ sx: { color: '#f0f6fc' } }}
              />
              <TextField
                fullWidth
                size="small"
                label="Media asset IDs (comma-separated, optional)"
                placeholder="123, 124, 125"
                value={qaMediaIdsStr}
                onChange={(e) => setQaMediaIdsStr(e.target.value)}
                InputProps={{ sx: { color: '#f0f6fc', fontFamily: 'ui-monospace' } }}
              />
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Notes (optional)"
                value={qaRunNotes}
                onChange={(e) => setQaRunNotes(e.target.value)}
                InputProps={{ sx: { color: '#f0f6fc' } }}
              />
              <FormControl size="small" sx={{ maxWidth: 200 }}>
                <InputLabel sx={{ color: '#8b949e' }}>Tone rating</InputLabel>
                <Select
                  label="Tone rating"
                  value={qaRunRating === '' ? '' : String(qaRunRating)}
                  onChange={(e) => setQaRunRating(e.target.value)}
                  displayEmpty
                  sx={{ color: '#f0f6fc' }}
                >
                  <MenuItem value="">
                    <em>—</em>
                  </MenuItem>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <MenuItem key={n} value={String(n)}>
                      {n} — tone consistency
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                startIcon={qaSaving ? <CircularProgress size={18} /> : <Science />}
                disabled={qaSaving}
                onClick={handleLogCaptionQaRun}
                sx={{ borderColor: '#30363d', color: '#79c0ff', alignSelf: 'flex-start' }}
              >
                Log QA run (snapshots profile)
              </Button>
            </Stack>

            <Typography variant="subtitle2" sx={{ color: '#8b949e', mb: 1 }}>
              Recent runs
            </Typography>
            <DataTable
              empty={qaRuns.length === 0}
              emptyMessage="No runs logged yet."
              tableSx={{ '& .MuiTableCell-root': { borderColor: '#30363d' } }}
            >
              <TableHead>
                <TableRow sx={{ bgcolor: '#21262d' }}>
                  <TableCell sx={{ color: '#8b949e' }}>When</TableCell>
                  <TableCell sx={{ color: '#8b949e' }}>Label</TableCell>
                  <TableCell sx={{ color: '#8b949e' }}>Rating</TableCell>
                  <TableCell sx={{ color: '#8b949e' }}>Assets</TableCell>
                  <TableCell sx={{ color: '#8b949e' }}>Notes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {qaRuns.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell sx={{ color: '#c9d1d9', whiteSpace: 'nowrap' }}>
                      {r.created_at ? new Date(r.created_at).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell sx={{ color: '#f0f6fc' }}>{r.run_label}</TableCell>
                    <TableCell sx={{ color: '#c9d1d9' }}>
                      {r.tone_rating != null ? r.tone_rating : '—'}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: '#8b949e',
                        fontFamily: 'ui-monospace',
                        fontSize: 12,
                        maxWidth: 180,
                      }}
                    >
                      {Array.isArray(r.media_asset_ids) && r.media_asset_ids.length
                        ? r.media_asset_ids.join(', ')
                        : '—'}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: '#8b949e',
                        fontSize: 12,
                        maxWidth: 280,
                      }}
                    >
                      {(r.notes || '').slice(0, 200)}
                      {(r.notes || '').length > 200 ? '…' : ''}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </DataTable>
          </Paper>
        </Stack>
      </Box>
    </Box>
  );
}
